import React, { useState, useEffect } from "react";
import { Modal, Button, Badge, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  FaBell,
  FaBuilding,
  FaClipboardList,
  FaClock,
  FaComments,
  FaDownload,
  FaExclamationTriangle,
  FaEye,
  FaFileCode,
  FaFileMedical,
  FaFilePdf,
  FaHistory,
  FaReply,
  FaUserMd,
  FaUserTie,
} from "react-icons/fa";
import IconButton from "components/common/IconButton";
import { Reply } from "react-bootstrap-icons";
import SendCommunicationRequestModal from "./SendCommunicationRequestModal";
import SendClaimCommunicationModal from "./SendClaimCommunicationModal";

const ClaimCommunicationsListModal = ({ show, onHide, modalRow }) => {
  const { claimCoummunicationsList } = useSelector((state) => state.claim);

  const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));
  const [activeFilter, setActiveFilter] = useState("all");
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [messageStats, setMessageStats] = useState({
    total: 0,
    urgent: 0,
    pending: 0,
    completed: 0,
  });

  const [showCommunicationModal, setShowCommunicationModal] = useState(false);
  const [selectedCommunicationId, setSelectedCommunicationId] = useState(null);

  useEffect(() => {
    if (claimCoummunicationsList) {
      // Calculate statistics
      const stats = {
        total: claimCoummunicationsList.length,
        urgent: claimCoummunicationsList.filter(
          (item) => item.comun_prior?.toLowerCase() === "asap",
        ).length,
        pending: claimCoummunicationsList.filter(
          (item) => item.comun_prior?.toLowerCase() === "reminder",
        ).length,
        completed: claimCoummunicationsList.filter(
          (item) => item.comun_prior?.toLowerCase() === "instruction",
        ).length,
      };
      setMessageStats(stats);

      // Apply initial filter
      filterMessages("all");
    }
  }, [claimCoummunicationsList]);

  const filterMessages = (type) => {
    setActiveFilter(type);

    if (type === "all") {
      setFilteredMessages(claimCoummunicationsList || []);
      return;
    }

    const filtered = claimCoummunicationsList.filter((item) => {
      const priority = item.comun_prior?.toLowerCase();

      switch (type) {
        case "urgent":
          return priority === "asap";
        case "pending":
          return priority === "reminder";
        case "completed":
          return priority === "instruction";
        default:
          return true;
      }
    });

    setFilteredMessages(filtered || []);
  };

  const handleDownload = async (url, fileName = "attachment") => {
    try {
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Failed to download file.");
    }
  };

  const getPriorityColor = (priority) => {
    const variants = {
      asap: "danger",
      urgent: "warning",
      routine: "primary",
      stat: "info",
      reminder: "warning",
      instruction: "success",
    };
    return variants[priority?.toLowerCase()] || "secondary";
  };

  const getCategoryColor = (category) => {
    const variants = {
      reminder: "success",
      instruction: "primary",
      alert: "danger",
      notification: "info",
    };
    return variants[category?.toLowerCase()] || "secondary";
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        scrollable
        className="communications-modal custom-communications-modal"
      >
        <Modal.Header
          closeButton
          className="text-info custom-shadow-sm text-800 border-bottom-2 border-info"
        >
          <Modal.Title className="h4 mb-0 fs-9 d-flex align-items-center">
            <div className="header-icon fs-11 me-3">
              <FaComments className="text-info" />
            </div>
            Communications History
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 messages-scroll">
          {filteredMessages.length > 0 ? (
            filteredMessages.map((item, index) => {
              const isOutgoing = item.msgnature?.toLowerCase() === "sent";
              const senderName = isOutgoing
                ? selectedBranch?.branch_name || selectedBranch?.label
                : modalRow?.payer_name;
              const senderType = isOutgoing ? "Branch" : "Insurance Company";
              const initials = getInitials(senderName || "Unknown");

              let message = "";
              try {
                if (item?.comun_recvrsn) {
                  const parsed = JSON.parse(item.comun_recvrsn);
                  if (Array.isArray(parsed)) {
                    message = parsed
                      .map((m) => m?.contentString)
                      .filter(Boolean)
                      .join(" , ");
                  } else {
                    message = item.comun_recvrsn;
                  }
                }
              } catch (e) {
                message = item?.comun_recvrsn;
              }

              return (
                <div
                  key={index}
                  className={`mb-4 d-flex ${
                    isOutgoing ? "justify-content-end" : "justify-content-start"
                  }`}
                >
                  <div
                    className={`message-bubble rounded-4 ${
                      isOutgoing
                        ? "border border-success custom-shadow-sm"
                        : "border border-secondary custom-shadow-sm"
                    }`}
                  >
                    {/* Sender Info */}
                    <div className="d-flex align-items-center mb-3">
                      <div
                        className={`avatar me-3 ${
                          isOutgoing ? "outgoing-avatar" : "incoming-avatar"
                        }`}
                      >
                        {initials}
                      </div>
                      <div>
                        <div className="fw-semibold">{senderName || "N/A"}</div>
                        <small className="opacity-75">{senderType}</small>
                      </div>
                    </div>

                    {/* Priority, Category, Reason */}
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <div className="d-flex gap-2">
                        {item.comun_prior && (
                          <Badge className="custom-badge bg-danger">
                            {item.comun_prior || "N/A"}
                          </Badge>
                        )}
                        {item.comun_categ && (
                          <Badge className="custom-badge bg-primary">
                            {item.comun_categ || "N/A"}
                          </Badge>
                        )}
                        {(item.comun_cde || item?.comun_rsn) && (
                          <Badge className="custom-badge bg-secondary">
                            {item.comun_cde || item?.comun_rsn || "-"}
                          </Badge>
                        )}
                      </div>
                      <small className="opacity-75 d-flex align-items-center">
                        <FaClock className="me-1" />
                        {moment(item?.created_at).format(
                          "DD-MM-YYYY, HH:mm:ss",
                        )}
                      </small>
                    </div>

                    {/* Content */}
                    <div className="mb-3">
                      {item.comun_reqdesc || message ? (
                        <div
                          className="fs-11"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {item.comun_reqdesc && (
                            <div className="mb-1">{item.comun_reqdesc}</div>
                          )}
                          {message && <div>{message}</div>}
                        </div>
                      ) : (
                        <em className="opacity-75">No message content</em>
                      )}
                    </div>

                    {/* Attachments */}
                    {item.attach_fullurl && (
                      <div className="attachment-card border p-2 mb-2">
                        <div className="d-flex align-items-center">
                          <div className="attachment-icon border border-danger me-3">
                            {item.comun_prior?.toLowerCase() === "asap" ? (
                              <FaFileMedical className="text-danger" />
                            ) : item.comun_prior?.toLowerCase() ===
                              "instruction" ? (
                              <FaFileCode className="text-success" />
                            ) : (
                              <FaFilePdf className="text-primary" />
                            )}
                          </div>
                          <div className="flex-grow-1">
                            <div className="fw-medium small mb-1">
                              {decodeURIComponent(
                                item.attach_fullurl.split("/").pop(),
                              )}
                            </div>
                            <div
                              className="opacity-75"
                              style={{ fontSize: "11px" }}
                            >
                              Document • PDF
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip
                                  style={{
                                    "--bs-tooltip-bg": "var(--bs-primary)",
                                    "--bs-tooltip-color": "var(--bs-white)",
                                  }}
                                  className="rounded"
                                >
                                  View
                                </Tooltip>
                              }
                            >
                              <a
                                href={item.attach_fullurl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="action-btn text-info"
                              >
                                <FaEye />
                              </a>
                            </OverlayTrigger>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip
                                  style={{
                                    "--bs-tooltip-bg": "var(--bs-info)",
                                    "--bs-tooltip-color": "var(--bs-white)",
                                  }}
                                  className="rounded"
                                >
                                  Download
                                </Tooltip>
                              }
                            >
                              <span
                                className="action-btn text-success cursor-pointer"
                                onClick={() =>
                                  handleDownload(
                                    item.attach_fullurl,
                                    decodeURIComponent(
                                      item.attach_fullurl.split("/").pop(),
                                    ),
                                  )
                                }
                              >
                                <FaDownload />
                              </span>
                            </OverlayTrigger>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Reply Button for Incoming Messages */}
                    {!isOutgoing && (
                      <Button
                        variant="primary"
                        size="sm"
                        className="btn-reply mt-3"
                        onClick={() => {
                          setSelectedCommunicationId(item.comun_recvreqid);
                          setShowCommunicationModal(true);
                          onHide();
                        }}
                      >
                        <FaReply className="me-1" />
                        Reply
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-5">
              <div className="mb-3">
                <span className="fs-3 text-400">
                  <span className="far fa-envelope" />
                </span>
              </div>
              <h5 className="fw-semi-bold">No messages found</h5>
              <p className="text-600">Start a conversation with your payer</p>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Reply Modal */}
      <SendClaimCommunicationModal
        modal={showCommunicationModal}
        setModal={setShowCommunicationModal}
        setSelectedCommunicationId={setSelectedCommunicationId}
        modalRow={modalRow}
        parentCommunicationId={selectedCommunicationId}
      />

      {/* Custom Styles */}
      <style jsx="true">{`
        .communications-modal .modal-content {
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(10px);
          border: none;
          max-height: 600px;
        }

        .header-icon {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-header {
          height: 60px;
        }
        .messages-scroll {
          max-height: 70vh;
          overflow-y: auto;
        }

        .message-bubble {
          padding: 18px 22px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
          max-width: 70%;
        }

        .outgoing-bubble {
          background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
          color: white;
          border-bottom-right-radius: 8px;
        }

        .avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
        }

        .incoming-avatar {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        .outgoing-avatar {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
        }

        .attachment-card {
          border-radius: 12px;
          transition: all 0.2s ease;
          cursor: pointer;
          border: 1px solid rgba(0, 0, 0, 0.1);
        }

        .outgoing-bubble .attachment-card {
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .outgoing-bubble .attachment-card:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .attachment-icon {
          width: 24px;
          height: 24px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 12px;
          font-weight: 600;
        }

        .action-btn {
          width: 24px;
          height: 24px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          background: rgba(0, 0, 0, 0.1);
          color: inherit;
        }

        .action-btn:hover {
          transform: scale(1.1);
          background: rgba(0, 0, 0, 0.2);
          color: inherit;
        }

        .custom-badge {
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 4px 10px;
        }

        .btn-reply {
          background: #2c7be5;
          border: none;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .btn-reply:hover {
          background: #1c64c7;
          transform: translateY(-1px);
        }

        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </>
  );
};

export default ClaimCommunicationsListModal;
